import { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play } from "lucide-react";
import { PageHero } from "@/components/bits";
import Lightbox from "@/components/Lightbox";
import { GALLERY_ITEMS } from "@/data/site";

const Gallery = () => {
  const cats = useMemo(() => ["All", ...new Set(GALLERY_ITEMS.map((g) => g.cat))], []);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = active === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.cat === active);

  const spans = [
    "sm:col-span-2 sm:row-span-2 aspect-square",
    "aspect-[4/3]",
    "aspect-[4/3]",
    "sm:row-span-2 aspect-[3/4]",
    "aspect-[4/3]",
    "sm:col-span-2 aspect-[16/9]",
    "aspect-[4/3]",
    "aspect-square",
    "aspect-[4/3]",
    "sm:col-span-2 aspect-[16/9]",
  ];

  return (
    <>
      <PageHero
        eyebrow="Gallery"
        title={<>Inside the <span className="text-red-500">academy</span></>}
        sub="Classes, events, performances, behind the scenes and everyday academy moments — including real photos and video from our own studio."
      />

      <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8" data-testid="gallery-grid">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Gallery categories">
          {cats.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={active === c}
              onClick={() => setActive(c)}
              className={`border px-5 py-2.5 font-display text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all ${
                active === c ? "border-red-600 bg-red-600 text-white" : "border-white/15 text-zinc-400 hover:border-red-500/60 hover:text-white"
              }`}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4" style={{ gridAutoFlow: "dense" }}>
          <AnimatePresence mode="popLayout">
            {filtered.map((g, i) => (
              <motion.button
                layout
                key={g.img + g.cat}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.04 }}
                className={`img-zoom group relative overflow-hidden border border-white/10 ${spans[i % spans.length]}`}
                onClick={() => setLightbox(i)}
                aria-label={`View ${g.caption}`}
                data-testid={`gallery-item-${i}`}
              >
                <img src={g.img} alt={g.caption} className="h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 transition-colors duration-500 group-hover:bg-black/45" aria-hidden="true" />
                {g.type === "video" && (
                  <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/40 bg-black/55 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
                      <Play size={22} className="ml-0.5 text-red-500" fill="currentColor" />
                    </span>
                  </span>
                )}
                <p className="absolute bottom-4 left-4 font-mono2 text-[9px] uppercase tracking-[0.24em] text-red-400 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  {g.type === "video" ? "Video · " : ""}{g.cat}
                </p>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <Lightbox items={filtered} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
    </>
  );
};

export default Gallery;
